/**
 * Outlier sanitization for map marker locations.
 *
 * Marker locations on the project map are derived from several sources of
 * varying trust: a record's own lat/long, GPS geometry, as-built sources, or —
 * as a last resort — the EXIF GPS of an attached photo. EXIF GPS in particular
 * is frequently wrong (cameras with no fix, corrupted metadata), which can beach
 * a marker thousands of miles away in the ocean.
 *
 * A location is treated as an outlier only when it sits too far from EVERY
 * project site (nearest-site check, not a single centroid). This keeps projects
 * whose sites are spread across a wide area — e.g. a city-wide or corridor job —
 * working correctly: a marker near any one site is kept, while bad GPS that is
 * far from all sites is dropped to "no location".
 */

export interface GeoPoint {
  latitude: number;
  longitude: number;
}

/**
 * Max distance (in degrees) a derived marker location may sit from its nearest
 * project site before it is treated as a bad-GPS outlier. 1 degree ≈ 69 miles
 * at the equator, so this is roughly 100 miles.
 */
export const MAX_LOCATION_OUTLIER_DEGREES = 1.5;

/**
 * Parse a coordinate value (string or number) into a finite number, or null
 * when it is empty/non-numeric. Coordinates arrive from the API as strings.
 */
function toFiniteCoord(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : parseFloat(value as string);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Reference points used to detect outliers: every project site that has valid
 * coordinates, falling back to the project location when no site does. Returns
 * an empty array when neither is available, in which case callers should skip
 * sanitization rather than drop every marker.
 */
export function computeOutlierReferences(
  sites: { latitude?: unknown; longitude?: unknown }[] | null | undefined,
  project: { latitude?: unknown; longitude?: unknown } | null | undefined,
): GeoPoint[] {
  const sitePoints = (sites || [])
    .map(s => ({ latitude: toFiniteCoord(s?.latitude), longitude: toFiniteCoord(s?.longitude) }))
    .filter((c): c is GeoPoint => c.latitude !== null && c.longitude !== null);

  if (sitePoints.length > 0) {
    return sitePoints;
  }

  const projLat = toFiniteCoord(project?.latitude);
  const projLon = toFiniteCoord(project?.longitude);
  if (projLat !== null && projLon !== null) {
    return [{ latitude: projLat, longitude: projLon }];
  }

  return [];
}

/**
 * True when `location` lies more than `maxDegrees` from EVERY reference on at
 * least one axis — i.e. bad GPS/EXIF that is near none of the project sites.
 * An empty `references` list means "no reference available", so nothing is
 * considered an outlier.
 */
export function isOutlierLocation(
  location: GeoPoint,
  references: GeoPoint[],
  maxDegrees: number = MAX_LOCATION_OUTLIER_DEGREES,
): boolean {
  if (!references || references.length === 0) {
    return false;
  }
  const nearAnySite = references.some(ref =>
    Math.abs(location.latitude - ref.latitude) <= maxDegrees &&
    Math.abs(location.longitude - ref.longitude) <= maxDegrees,
  );
  return !nearAnySite;
}

/**
 * Returns `location` unchanged when it is a valid, non-outlier point (within
 * `maxDegrees` of its nearest reference); returns null when it is missing,
 * non-finite, or an outlier so callers treat it as "no location". When
 * `references` is empty, sanitization is skipped and a finite location is
 * returned as-is.
 */
export function rejectOutlierLocation(
  location: GeoPoint | null | undefined,
  references: GeoPoint[],
  maxDegrees: number = MAX_LOCATION_OUTLIER_DEGREES,
): GeoPoint | null {
  if (!location || !Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) {
    return null;
  }
  return isOutlierLocation(location, references, maxDegrees) ? null : location;
}
